import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const KitchenReports = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/admin/kitchen/reports/performance?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`);
      if (data.success) setReportData(data.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
    setLoading(false);
  };

  const styles = {
    container: {
      padding: isMobile ? '16px 4px' : '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: isMobile ? '16px' : '24px'
    },
    title: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    dateFilter: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: '12px',
      marginBottom: isMobile ? '16px' : '20px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    input: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      textAlign: 'center'
    },
    statValue: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: '800',
      marginBottom: '8px',
      color: colors.primary
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      marginBottom: isMobile ? '16px' : '20px'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: isMobile ? '10px 8px' : '12px 16px',
      textAlign: 'left',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '700',
      color: colors.textLight,
      textTransform: 'uppercase',
      borderBottom: `2px solid ${colors.lightGray}`
    },
    td: {
      padding: isMobile ? '10px 8px' : '12px 16px',
      fontSize: isMobile ? '12px' : '13px',
      borderBottom: `1px solid ${colors.lightGray}`
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading reports...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!reportData) {
    return (
      <AdminLayout>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.textLight }}>
            No data available
          </div>
        </div>
      </AdminLayout>
    );
  }

  const totalItems = reportData.itemsPrepared.reduce((sum, day) => sum + day.items_count, 0);
  const totalRevenue = reportData.itemsPrepared.reduce((sum, day) => sum + parseFloat(day.revenue), 0);

  return (
    <AdminLayout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Kitchen Reports</h1>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
            Kitchen sales analytics, performance reports, and operational insights
          </p>
        </div>

        {/* Date Filter */}
        <div style={styles.dateFilter}>
          <label style={{ fontSize: '13px', fontWeight: '600' }}>From:</label>
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            style={styles.input}
          />

          <label style={{ fontSize: '13px', fontWeight: '600' }}>To:</label>
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            style={styles.input}
          />

          <button
            onClick={fetchReports}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: colors.primary,
              color: colors.white,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Summary Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{totalItems}</div>
            <div style={styles.statLabel}>Total Items Prepared</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>₹{totalRevenue.toLocaleString('en-IN')}</div>
            <div style={styles.statLabel}>Total Revenue</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {Math.round(reportData.avgPrepTime?.avg_minutes || 0)} min
            </div>
            <div style={styles.statLabel}>Avg Prep Time</div>
          </div>
        </div>

        {/* Top Selling Items */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>🏆</span>
            <span>Top Selling Items</span>
          </h2>

          {reportData.topItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No sales data for selected period
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Rank</th>
                    <th style={styles.th}>Item Name</th>
                    <th style={styles.th}>Orders</th>
                    {!isMobile && <th style={styles.th}>Quantity</th>}
                    <th style={styles.th}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.topItems.map((item, index) => (
                    <tr key={index}>
                      <td style={styles.td}>
                        <strong style={{
                          fontSize: '16px',
                          color: index < 3 ? colors.primary : colors.textDark
                        }}>
                          #{index + 1}
                        </strong>
                      </td>
                      <td style={styles.td}>
                        <strong>{item.item_name}</strong>
                      </td>
                      <td style={styles.td}>{item.times_ordered}</td>
                      {!isMobile && <td style={styles.td}>{item.total_quantity}</td>}
                      <td style={styles.td}>
                        <strong style={{ color: colors.primary }}>
                          ₹{parseFloat(item.total_revenue).toLocaleString('en-IN')}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Daily Performance */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>📊</span>
            <span>Daily Performance</span>
          </h2>

          {reportData.itemsPrepared.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
              No performance data for selected period
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Items Prepared</th>
                    <th style={styles.th}>Revenue</th>
                    {!isMobile && <th style={styles.th}>Avg/Item</th>}
                  </tr>
                </thead>
                <tbody>
                  {reportData.itemsPrepared.map((day, index) => {
                    const avgPerItem = day.items_count > 0 ? parseFloat(day.revenue) / day.items_count : 0;

                    return (
                      <tr key={index}>
                        <td style={styles.td}>
                          <strong>
                            {new Date(day.date).toLocaleDateString('en-IN', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </strong>
                        </td>
                        <td style={styles.td}>{day.items_count} items</td>
                        <td style={styles.td}>
                          <strong style={{ color: colors.primary }}>
                            ₹{parseFloat(day.revenue).toLocaleString('en-IN')}
                          </strong>
                        </td>
                        {!isMobile && (
                          <td style={styles.td}>
                            ₹{avgPerItem.toFixed(2)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Performance Insights */}
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>⚡</span>
            <span>Performance Insights</span>
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' }}>
            <div style={{
              padding: '16px',
              background: colors.lightGray,
              borderRadius: '8px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                AVERAGE PREPARATION TIME
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <span style={{ fontSize: '24px', fontWeight: '700', color: colors.primary }}>
                  {Math.round(reportData.avgPrepTime?.avg_minutes || 0)}
                </span>
                <span style={{ fontSize: '14px', color: colors.textLight }}>minutes</span>
              </div>
              <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '8px' }}>
                Min: {Math.round(reportData.avgPrepTime?.min_minutes || 0)} min •
                Max: {Math.round(reportData.avgPrepTime?.max_minutes || 0)} min
              </div>
            </div>

            <div style={{
              padding: '16px',
              background: colors.lightGray,
              borderRadius: '8px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                DAILY AVERAGE
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <span style={{ fontSize: '24px', fontWeight: '700', color: colors.primary }}>
                  {reportData.itemsPrepared.length > 0
                    ? Math.round(totalItems / reportData.itemsPrepared.length)
                    : 0}
                </span>
                <span style={{ fontSize: '14px', color: colors.textLight }}>items/day</span>
              </div>
              <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '8px' }}>
                Across {reportData.itemsPrepared.length} days
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default KitchenReports;
